<script setup lang="ts">
import { Icon } from '@iconify/vue';
import { computed, onMounted, ref, useId } from 'vue';

interface Props {
   label?: string;
   placeholder?: string;
   type?: 'text' | 'password' | 'email' | 'number' | 'tel';
   required?: boolean;
   disabled?: boolean;
   errorMessage?: string;
   readonly?: boolean;
   paddingSize?: 'md' | 'lg';
}

const props = withDefaults(defineProps<Props>(), {
   type: 'text',
   required: false,
   disabled: false,
   errorMessage: '',
   readonly: false,
   paddingSize: 'md',
});

const model = defineModel<string>();
const inputType = ref();
const inputRef = ref<HTMLInputElement | null>(null);

const id = useId();

defineExpose({
   focus: () => inputRef.value?.focus(),
   select: () => inputRef.value?.select(),
});

onMounted(() => {
   inputType.value = props.type;
});

// Horizontal padding sizes
// md: current/default value (px-4)
// lg: double the md value (px-8)
const paddingClasses = {
   md: 'px-4', // current/default
   lg: 'px-8', // double of md
};

const inputStyle = computed(() => {
   const baseClasses = [
      'input input-bordered flex items-center gap-2 rounded-full w-full h-12',
      'focus-within:outline-none border-0 ring-1 focus-within:border-0 focus-within:ring-2',
      'transition-all duration-100',
      paddingClasses[props.paddingSize],
   ];
   if (!props.errorMessage) {
      baseClasses.push('focus-within:ring-primary ring-primary');
   }
   else {
      baseClasses.push('focus-within:ring-accent ring-accent');
   }
   return baseClasses.join(' ');
});

function toggleInputType() {
   if (inputType.value === 'text')
      inputType.value = 'password';
   else inputType.value = 'text';
}
</script>

<template>
   <div class="form-control w-full">
      <label v-if="label" :for="id" class="label">
         <span class="label-text">{{ label }}</span>
         <span v-if="required" class="text-accent">*</span>
      </label>

      <div :class="inputStyle">
         <!-- leading content -->
         <slot name="leading" />

         <input
            :id="id"
            ref="inputRef"
            v-model="model"
            :placeholder="placeholder"
            :required="required"
            :disabled="disabled"
            :aria-label="placeholder || label || 'Text input'"
            :type="inputType"
            class="grow max-w-full read-only:cursor-not-allowed"
            :readonly="readonly"
            :name="id"
         />

         <!-- password toggle button -->
         <button
            v-if="type === 'password'"
            type="button"
            class="hover:bg-slate-100 p-2 rounded-full group"
            :aria-label="inputType === 'text' ? 'Hide password' : 'Show password'"
            @click="toggleInputType"
         >
            <Icon
               :icon="inputType === 'text' ? 'garden:eye-stroke-16' : 'garden:eye-hide-stroke-16'"
               class="size-5 text-slate-400 group-hover:text-slate-500"
            />
         </button>

         <!-- trailing content -->
         <template v-else>
            <slot name="trailing" />
         </template>
      </div>

      <!-- error message -->
      <div v-if="errorMessage" class="text-accent text-sm mt-2 ml-4">
         {{ errorMessage }}
      </div>
   </div>
</template>
